import {Filter, ProviderSupportStatus} from '@github-ui/filter'
import {
  AssigneeFilterProvider,
  IsFilterProvider,
  LabelFilterProvider,
  MilestoneFilterProvider,
  RepositoryFilterProvider,
  StateFilterProvider,
} from '@github-ui/filter/providers'
import {memo, useMemo} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {getInitialState} from '../../helpers/initial-state'
import type {LoggedInUser} from '../../helpers/json-island'
import type {ColumnModel} from '../../models/column-model'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {Resources} from '../../strings'
import {
  DateFilterProvider,
  FILTER_PRIORITIES,
  IterationFilterProvider,
  LinkedPullRequestsFilterProvider,
  MemexTypeProvider,
  NoFilterProvider,
  NumberFilterProvider,
  ReviewersFilterProvider,
  SingleSelectFilterProvider,
  TextFilterProvider,
} from './filter-providers'

type FilterBarProps = {
  value?: string
  onChange: (value: string) => void
}

const SUPPORTED_FILTER_TYPES: Array<MemexColumnDataType> = [
  MemexColumnDataType.Assignees,
  MemexColumnDataType.Date,
  MemexColumnDataType.Iteration,
  MemexColumnDataType.Labels,
  MemexColumnDataType.LinkedPullRequests,
  MemexColumnDataType.Milestone,
  MemexColumnDataType.IssueType,
  MemexColumnDataType.Number,
  MemexColumnDataType.Repository,
  MemexColumnDataType.Reviewers,
  MemexColumnDataType.SingleSelect,
  MemexColumnDataType.Text,
]

const getFilterProviderForColumn = (column: ColumnModel, loggedInUser?: LoggedInUser) => {
  const userFilterParams = loggedInUser
    ? {
        showAtMe: true,
        currentUserLogin: loggedInUser.login,
        currentUserAvatarUrl: loggedInUser.avatarUrl,
      }
    : {showAtMe: false}

  // Setting valueless to false for all because we don't support the e.g. `assignee:none` syntax yet
  switch (column.dataType) {
    case MemexColumnDataType.Assignees:
      return new AssigneeFilterProvider(userFilterParams, {filterTypes: {valueless: false}})
    case MemexColumnDataType.Date:
      return new DateFilterProvider(column)
    case MemexColumnDataType.Iteration:
      return new IterationFilterProvider(column)
    case MemexColumnDataType.Labels:
      return new LabelFilterProvider(undefined, {filterTypes: {valueless: false}})
    case MemexColumnDataType.LinkedPullRequests:
      return new LinkedPullRequestsFilterProvider({filterTypes: {valueless: false}})
    case MemexColumnDataType.Milestone:
      return new MilestoneFilterProvider(undefined, {
        priority: FILTER_PRIORITIES.milestone,
        filterTypes: {valueless: false},
      })
    case MemexColumnDataType.IssueType:
      return new MemexTypeProvider()
    case MemexColumnDataType.Number:
      return new NumberFilterProvider(column)
    case MemexColumnDataType.Repository:
      return new RepositoryFilterProvider({filterTypes: {valueless: false}})
    case MemexColumnDataType.Reviewers:
      return new ReviewersFilterProvider(userFilterParams, {filterTypes: {valueless: false}})
    case MemexColumnDataType.SingleSelect:
      return new SingleSelectFilterProvider(column)
    case MemexColumnDataType.Text:
      return new TextFilterProvider(column)
    default:
      throw new Error(`Filter support for ${column.name} column not implemented`)
  }
}

const filterBarSx = {width: '100%'}
export const FilterBar = memo(function FilterBar({value, onChange}: FilterBarProps) {
  const {allColumns} = useAllColumns()
  const {loggedInUser} = getInitialState()
  const columnFilterProviders = useMemo(
    () =>
      allColumns
        .filter(({dataType}) => SUPPORTED_FILTER_TYPES.includes(dataType))
        .map(column => getFilterProviderForColumn(column, loggedInUser)),
    [allColumns, loggedInUser],
  )

  return (
    <Filter
      id="filter-bar-component"
      data-testid="filter-bar-component"
      label="Filter"
      filterButtonVariant="compact"
      showValidationMessage={false}
      settings={{
        aliasMatching: true,
      }}
      aria-label={Resources.filterByKeyboardOrByField}
      placeholder={Resources.filterByKeyboardOrByField}
      sx={filterBarSx}
      filterValue={value}
      onChange={onChange}
      providers={useMemo(
        () => [
          ...columnFilterProviders,
          new NoFilterProvider(columnFilterProviders),
          new IsFilterProvider(['issue', 'pr', 'open', 'closed', 'draft', 'merged'], {filterTypes: {valueless: false}}),
          new StateFilterProvider('mixed', {
            priority: FILTER_PRIORITIES.state,
            support: {status: ProviderSupportStatus.Unsupported},
            filterTypes: {valueless: false},
          }),
        ],
        [columnFilterProviders],
      )}
    />
  )
})

try{ FilterBar.displayName ||= 'FilterBar' } catch {}